import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCampaigns, Campaign } from '../services/campaignsService';
import { apiGet, apiPost } from '../services/api';
import '../styles/FormPages.css';

interface DonationForm {
  donorID: string;
  donationType: 'Treasury' | 'Volunteer Hours' | 'In-Kind';
  amount: string;
  donationDate: string;
  donationFrequency: string;
  paymentMethod: string;
  campaignID: string;
  description: string;
  notes: string;
}

interface Donor {
  donorID: number;
  fullName: string;
  phoneNumber?: string;
}

export function RecordDonationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [donors, setDonors] = useState<Donor[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<DonationForm>({
    donorID: '',
    donationType: 'Treasury',
    amount: '',
    donationDate: new Date().toISOString().split('T')[0],
    donationFrequency: 'One-Time',
    paymentMethod: 'Check',
    campaignID: '',
    description: '',
    notes: '',
  });

  useEffect(() => {
    // Fetch donors and campaigns
    const fetchData = async () => {
      try {
        // Fetch donors using API client
        const donorsResponse = await apiGet<Donor[]>('/donors');
        if (donorsResponse.success) {
          setDonors(donorsResponse.data || []);
        } else {
          setError('Failed to load donors. Please refresh the page to try again.');
        }

        // Fetch active campaigns
        const campaignsData = await getCampaigns();
        // Filter for active campaigns only
        const activeCampaigns = campaignsData.filter((c) => c.isActive);
        setCampaigns(activeCampaigns);
      } catch (err: unknown) {
        setError('Failed to load data. Please refresh the page to try again.');
      }
    };

    fetchData();
  }, []);

  const filteredDonors = donors.filter(
    (donor) =>
      donor.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (donor.phoneNumber && donor.phoneNumber.includes(searchTerm))
  );

  const selectedDonor = donors.find((donor) => donor.donorID === parseInt(formData.donorID));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const donationData: Record<string, unknown> = {
        donorID: parseInt(formData.donorID),
        donationType: formData.donationType,
        amount: parseFloat(formData.amount),
        donationDate: formData.donationDate,
        donationFrequency: formData.donationFrequency,
        paymentMethod: formData.paymentMethod,
        description: formData.description || null,
        notes: formData.notes || null,
      };

      // Only include campaignID if a campaign was selected
      if (formData.campaignID && formData.campaignID !== '') {
        donationData.campaignID = parseInt(formData.campaignID);
      }

      const response = await apiPost('/donations', donationData);

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to record donation');
      }

      navigate('/donors');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-page">
      <div className="page-header">
        <h2>Record Donation</h2>
        <p>Log a new donation to the system</p>
      </div>

      <form onSubmit={handleSubmit} className="intake-form">
        {error && <div className="alert alert-error">{error}</div>}

        <div className="form-section">
          <h3>Donation Information</h3>

          <div className="form-group">
            <label htmlFor="search">Search for Donor *</label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or phone number..."
              className="search-input"
            />
          </div>

          {selectedDonor && (
            <div className="selected-individual">
              <div className="selected-label">Selected Donor:</div>
              <div className="individual-info">
                <strong>{selectedDonor.fullName}</strong>
              </div>
            </div>
          )}

          <div
            className="individuals-list"
            style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '10px',
              marginBottom: '20px',
            }}
          >
            {filteredDonors.length === 0 ? (
              <div className="empty-state">
                {searchTerm ? (
                  <p>No donors found matching &quot;{searchTerm}&quot;</p>
                ) : donors.length === 0 ? (
                  <p>
                    No donors available.{' '}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => navigate('/donors/add')}
                    >
                      Add a donor first
                    </button>
                  </p>
                ) : (
                  <p>No donors found</p>
                )}
              </div>
            ) : (
              filteredDonors.map((donor) => (
                <label
                  key={donor.donorID}
                  className="individual-option"
                  style={{
                    display: 'block',
                    padding: '10px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <input
                    type="radio"
                    name="donor"
                    value={donor.donorID}
                    checked={formData.donorID === donor.donorID.toString()}
                    onChange={(e) => setFormData({ ...formData, donorID: e.target.value })}
                    style={{ marginRight: '10px' }}
                  />
                  <div style={{ display: 'inline-block' }}>
                    <div>{donor.fullName}</div>
                    {donor.phoneNumber && (
                      <div style={{ fontSize: '0.9em', color: '#666' }}>{donor.phoneNumber}</div>
                    )}
                  </div>
                </label>
              ))
            )}
          </div>

          <div className="form-group">
            <label htmlFor="donationType">Donation Type *</label>
            <select
              id="donationType"
              value={formData.donationType}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  donationType: e.target.value as DonationForm['donationType'],
                })
              }
              required
            >
              <option value="Treasury">Treasury (Monetary)</option>
              <option value="Volunteer Hours">Volunteer Hours</option>
              <option value="In-Kind">In-Kind (Goods/Services)</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="amount">
                {formData.donationType === 'Volunteer Hours' ? 'Hours *' : 'Amount (USD) *'}
              </label>
              <input
                type="number"
                id="amount"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                min="0.01"
                step={formData.donationType === 'Volunteer Hours' ? '0.5' : '0.01'}
                placeholder={formData.donationType === 'Volunteer Hours' ? '0.0' : '0.00'}
                required
              />
              {formData.donationType === 'Volunteer Hours' && (
                <small
                  style={{
                    color: '#666',
                    fontSize: '0.85rem',
                    marginTop: '0.25rem',
                    display: 'block',
                  }}
                >
                  Enter the number of volunteer hours contributed
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="donationDate">Donation Date *</label>
              <input
                type="date"
                id="donationDate"
                value={formData.donationDate}
                onChange={(e) => setFormData({ ...formData, donationDate: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          </div>

          {(formData.donationType === 'Volunteer Hours' || formData.donationType === 'In-Kind') && (
            <div className="form-group">
              <label htmlFor="description">
                {formData.donationType === 'Volunteer Hours'
                  ? 'Description of Service *'
                  : 'Description of Goods/Services *'}
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                placeholder={
                  formData.donationType === 'Volunteer Hours'
                    ? 'Describe the volunteer work performed...'
                    : 'Describe the donated goods or services...'
                }
                required
              />
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="donationFrequency">Frequency *</label>
              <select
                id="donationFrequency"
                value={formData.donationFrequency}
                onChange={(e) => setFormData({ ...formData, donationFrequency: e.target.value })}
                required
              >
                <option value="One-Time">One-Time</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="paymentMethod">Payment Method *</label>
              <select
                id="paymentMethod"
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                required
              >
                <option value="Cash">Cash</option>
                <option value="Check">Check</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Online">Online</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="campaignID">Associated Campaign</label>
            <select
              id="campaignID"
              value={formData.campaignID}
              onChange={(e) => setFormData({ ...formData, campaignID: e.target.value })}
            >
              <option value="">None</option>
              {campaigns.map((campaign) => (
                <option key={campaign.campaignID} value={campaign.campaignID}>
                  {campaign.campaignName}
                  {campaign.goalAmount && ` (Goal: $${campaign.goalAmount.toLocaleString()})`}
                </option>
              ))}
            </select>
            {campaigns.length === 0 && (
              <small
                style={{
                  color: '#666',
                  fontSize: '0.85rem',
                  marginTop: '0.25rem',
                  display: 'block',
                }}
              >
                No active campaigns. Create one in{' '}
                <button
                  type="button"
                  onClick={() => navigate('/admin/config')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#5B2C86',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  System Configuration
                </button>
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Additional details about this donation..."
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="button-secondary" onClick={() => navigate('/donors')}>
            Cancel
          </button>
          <button
            type="submit"
            className="button-primary"
            disabled={loading || donors.length === 0}
          >
            {loading ? 'Recording Donation...' : 'Record Donation'}
          </button>
        </div>
      </form>
    </div>
  );
}
